import { useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../../api/client';
import type { Me } from '../../api/types';
import { IconInfo } from '../../shared/icons';
import { AuthLayout } from './AuthLayout';

interface LoginPageProps {
  /** Called with the boot payload the login response already contains, so the
   *  app does not need a second round trip to /me. */
  onSignedIn: (me: Me) => void;
  onForgotPassword: () => void;
  onSignUp: () => void;
}

export function LoginPage({ onSignedIn, onForgotPassword, onSignUp }: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // What this deployment offers. It is allowed to fail: a sign-in form with a
  // password field is the useful default, and the extra buttons simply stay
  // hidden.
  const methods = useQuery({
    queryKey: ['auth-methods'],
    queryFn: api.authMethods,
    retry: false,
    staleTime: Infinity,
  });

  const signIn = useMutation({
    mutationFn: () => api.login(email, password),
    onSuccess: onSignedIn,
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim() || !password) return;
    signIn.mutate();
  };

  const error = signIn.error instanceof ApiError ? signIn.error : null;
  const selfSignup = methods.data?.self_signup ?? false;
  // A refused sign-in is usually just a wrong password, but two of the
  // reasons are worth a calmer frame: nothing is wrong with the password, the
  // account is simply not open yet.
  const waiting = error?.code === 'pending_approval';

  return (
    <AuthLayout kicker="Account sign in" heading="Sign in">
      <form className="auth-card" onSubmit={handleSubmit} noValidate>
        {error && (
          <div className={`notice ${waiting ? 'notice-ok' : 'notice-error'}`} role="alert">
            <IconInfo size={14} />
            <span>{error.message}</span>
          </div>
        )}

        <div className="field">
          <label className="lbl" htmlFor="login-email">
            <span className="lbl-text">Work email</span>
          </label>
          <input
            id="login-email"
            className="inp"
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            required
            placeholder="name@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="lbl" htmlFor="login-password">
            <span className="lbl-text">Password</span>
            <button
              type="button"
              className="link-button"
              onClick={onForgotPassword}
            >
              Forgot?
            </button>
          </label>
          <input
            id="login-password"
            className="inp"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={signIn.isPending}>
          {signIn.isPending ? 'Signing in…' : 'Sign in'}
        </button>

        {selfSignup && (
          <button className="btn btn-block" type="button" onClick={onSignUp}>
            Create an account
          </button>
        )}
      </form>

      <div className="notice">
        <IconInfo size={14} />
        <span>
          {selfSignup
            ? 'Create an account, or use the invitation link EJOT sent you. Nobody but you ever sets your password.'
            : 'Accounts are created by EJOT, who send an invitation link. Nobody but you ever sets your password.'}
        </span>
      </div>
    </AuthLayout>
  );
}
